import React, { createContext, useContext, Component, ErrorInfo, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from './store';

import { useAuth } from './hooks/useAuth';
import { useDB, DBActions } from './lib/useDB';
import { SUPABASE_ENABLED } from './lib/supabase';
import { ParsedTournamentsProvider } from './lib/useParsedTournaments';
import { I18nProvider } from './lib/i18n';

import { Header } from './components/Header';
import { AuthPage } from './components/AuthPage';
import { Dashboard } from './components/Dashboard';
import { SessionForm } from './components/SessionForm';
import { SessionsTable } from './components/SessionsTable';
import { CalendarView } from './components/CalendarView';
import { GoalsPanel } from './components/GoalsPanel';
import { StakingView } from './components/StakingView';
import { ImportView } from './components/ImportView';
import { StatsView } from './components/StatsView';
import { UpgradeModal } from './components/UpgradeModal';
import { AnnouncementsModal } from './components/AnnouncementsModal';
import { useAnnouncements } from './hooks/useAnnouncements';
import { GuideView } from './components/GuideView';
import { DailyQuiz } from './components/DailyQuiz';
import { BlogView } from './components/BlogView';
import { TeamsView } from './components/TeamsView';
import { TeamConsentScreen } from './components/teams/TeamConsentScreen';
import { Footer } from './components/Footer';
import { BenchmarkProvider } from './contexts/BenchmarkContext';
import { usePlayerTeamBenchmarks } from './hooks/usePlayerTeamBenchmarks';

import './DarkTheme.css';

// ── Error Boundary — atrapa crashes de componentes y evita pantalla en blanco ──
interface EBState { hasError: boolean; error: Error | null }
class ErrorBoundary extends Component<{ children: ReactNode }, EBState> {
  state: EBState = { hasError: false, error: null };
  static getDerivedStateFromError(error: Error): EBState { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#111520', color: '#e8edf5', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
        <span style={{ fontSize: '1.5rem' }}>⚠</span>
        <p style={{ fontFamily: 'var(--mono)', fontSize: '0.85rem', color: '#e05252', maxWidth: 480 }}>
          {this.state.error?.message || 'Error inesperado'}
        </p>
        <button onClick={() => window.location.reload()} style={{ background: '#1e2636', border: '1px solid #2a3045', borderRadius: 6, color: '#e8edf5', padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.82rem' }}>
          Recargar
        </button>
      </div>
    );
  }
}

export type Tab = 'dashboard' | 'new' | 'sessions' | 'calendar' | 'goals' | 'staking' | 'import' | 'stats' | 'guide' | 'quiz' | 'blog' | 'teams';

export const DBContext = createContext<DBActions | null>(null);
export const useDBContext = () => useContext(DBContext)!;

function App() {
  const [tab, setTab] = React.useState<Tab>('dashboard');
  const [guideSection, setGuideSection] = React.useState<'guide' | 'faq' | 'stats'>('guide');
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [checkoutToast, setCheckoutToast] = React.useState<'success' | 'cancelled' | 'teams' | null>(null);
  const [teamToken, setTeamToken]         = React.useState<string | null>(
    () => sessionStorage.getItem('pendingTeamToken')
  );
  const [teamsKey, setTeamsKey]           = React.useState(0);
  const [teamsActivating, setTeamsActivating] = React.useState(false);
  const { user, loading: authLoading, plan, teamSeats, refreshPlan } = useAuth();
  const { announcements, dismiss: dismissAnnouncements } = useAnnouncements(user);
  const db = useDB(user);
  const teamBenchmarkOverrides = usePlayerTeamBenchmarks(user);

  const sessions = useSelector((s: RootState) => s.sessions.list);
  const settings = useSelector((s: RootState) => s.settings);

  const totalProfit = sessions.reduce((acc, s) => acc + s.netProfit, 0);
  const currentBankroll = settings.initialBankroll + totalProfit;

  // Handle Whop redirect back from checkout
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('checkout');
    if (!result) return;

    window.history.replaceState({}, '', window.location.pathname);

    if (result === 'cancelled') {
      setCheckoutToast('cancelled');
      const t = setTimeout(() => setCheckoutToast(null), 5000);
      return () => clearTimeout(t);
    }

    if (result === 'success') {
      setCheckoutToast('success');
      setTimeout(() => refreshPlan(), 2000);
      const t = setTimeout(() => setCheckoutToast(null), 5000);
      return () => clearTimeout(t);
    }

    // Teams checkout: navegar al tab Teams y hacer poll hasta que team_seats esté disponible
    if (result === 'teams') {
      setTab('teams');
      setCheckoutToast('teams');
      setTeamsActivating(true);

      let attempts = 0;
      const MAX_ATTEMPTS = 7;  // hasta ~10.5s
      const INTERVAL_MS  = 1500;

      const poll = async () => {
        attempts++;
        await refreshPlan();
        // refreshPlan actualiza teamSeats en el estado de useAuth.
        // No podemos leer el valor actualizado aquí directamente (closure stale),
        // así que delegamos el cierre del toast al efecto que observa teamSeats.
        if (attempts >= MAX_ATTEMPTS) clearInterval(intervalId);
      };

      const intervalId = setInterval(poll, INTERVAL_MS);
      return () => clearInterval(intervalId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando teamSeats llega tras el checkout Teams, cerrar el toast de activación
  React.useEffect(() => {
    if (teamsActivating && teamSeats && teamSeats > 0) {
      setTeamsActivating(false);
      // El toast ya visible se reemplaza con el éxito; se cierra sólo a los 6s
      const t = setTimeout(() => setCheckoutToast(null), 6000);
      return () => clearTimeout(t);
    }
  }, [teamSeats, teamsActivating]);

  // Handle team invite link: ?teamToken=xxx
  // Persiste en sessionStorage para sobrevivir reloads durante el flujo de auth.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('teamToken');
    if (token) {
      sessionStorage.setItem('pendingTeamToken', token);
      setTeamToken(token);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <div className="dz-spinner" style={{ width: 36, height: 36 }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.82rem', color: 'var(--muted)' }}>CARGANDO</span>
        </div>
      </div>
    );
  }

  if (SUPABASE_ENABLED && !user) return (
    <I18nProvider>
      <AuthPage />
    </I18nProvider>
  );
  
  const renderTab = () => {
    switch (tab) {
      case 'dashboard': return <Dashboard onNavigate={setTab} />;
      case 'new': return <SessionForm onSaved={() => setTab('dashboard')} />;
      case 'sessions': return <SessionsTable />;
      case 'calendar': return <CalendarView />;
      case 'goals': return <GoalsPanel />;
      case 'staking': return <StakingView />;
      case 'import': return <ImportView onViewStats={() => setTab('stats')} />;
      case 'stats': return <StatsView plan={plan} onUpgrade={() => setShowUpgrade(true)} />;
      case 'guide': return (<GuideView initialSection={guideSection} />);
      case 'quiz': return <DailyQuiz />;
      case 'blog': return <BlogView user={user} />;
      case 'teams': return <TeamsView key={teamsKey} user={user} plan={plan} teamSeats={teamSeats} onUpgrade={() => setShowUpgrade(true)} />;
      default: return <Dashboard onNavigate={setTab} />;
    }
  };

  return (
    <I18nProvider>
      <BenchmarkProvider overrides={teamBenchmarkOverrides}>
      <DBContext.Provider value={db}>
        <ParsedTournamentsProvider>
          <div className="app-root">
            <Header
              tab={tab}
              onTab={setTab}
              currentBankroll={currentBankroll}
              totalProfit={totalProfit}
              user={user}
              plan={plan}
              syncing={db.syncing}
              onUpgrade={() => setShowUpgrade(true)}
            />
            <main className="app-body">
              {renderTab()}
            </main>
            {checkoutToast && (
              <div className={`checkout-toast ${checkoutToast === 'cancelled' ? 'cancelled' : 'success'}`}>
                <span>
                  {checkoutToast === 'success'   && '🎉 ¡Suscripción activada! Ya tenés acceso a GrindLog Pro.'}
                  {checkoutToast === 'cancelled'  && '↩ Checkout cancelado. Tu plan no cambió.'}
                  {checkoutToast === 'teams' && !teamsActivating && teamSeats && teamSeats > 0
                    && '🎉 ¡Plan Teams activado! Ya podés crear tu equipo.'}
                  {checkoutToast === 'teams' && teamsActivating
                    && '⏳ Activando tu plan Teams...'}
                </span>
                <button className="checkout-toast-close" onClick={() => setCheckoutToast(null)}>✕</button>
              </div>
            )}

            {showUpgrade && <UpgradeModal user={user} onClose={() => setShowUpgrade(false)} />}
            {teamToken && user && (
              <TeamConsentScreen
                token={teamToken}
                user={user}
                onDone={(joined) => {
                  sessionStorage.removeItem('pendingTeamToken');
                  setTeamToken(null);
                  if (joined) {
                    setTeamsKey(k => k + 1); // fuerza remount de TeamsView → useTeam recarga
                    setTab('teams');
                  }
                }}
              />
            )}
            <AnnouncementsModal announcements={announcements} onDismiss={dismissAnnouncements} />
            <Footer
              onNavigate={setTab}
              onNavigateFaq={() => { setGuideSection('faq'); setTab('guide'); }}
            />
          </div>
        </ParsedTournamentsProvider>
      </DBContext.Provider>
      </BenchmarkProvider>
    </I18nProvider>
  );
}

export default function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
